import React, { useState, useEffect, useRef, SyntheticEvent, KeyboardEvent } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { isEmptyString } from '../../../utils/common';
import { SizeProps } from '../../../@types';
import { EMPTY_FIELD_MESSAGE } from '../../../constants/common';
import { LiveSearchBoxWithIdProps } from './LiveSearchBoxWithId.props';

export const LiveSearchBoxWithId: React.FC<LiveSearchBoxWithIdProps> = ({
  title,
  timeOffset,
  isError,
  value,
  inputRef,
  isRequired,
  helperText,
  defaultSuggetions,
  disabled,
  onClearValue,
  onChange,
  onApiInvoke,
  renderItem
}) => {
  const [options, setOptions] = useState<Array<any>>([]);
  const [inputValue, setInputValue] = useState('');
  const [helperTextValue, setHelperTextValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [delayedInput, setDelayedInput] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const timerRef = useRef<NodeJS.Timeout>(undefined);

  useEffect(() => {
    if (defaultSuggetions && defaultSuggetions.length) {
      setOptions(defaultSuggetions);
    }
  }, [defaultSuggetions]);

  useEffect(() => {
    if(delayedInput) {
      if(inputValue && (!defaultSuggetions || defaultSuggetions?.length == 0) && options.length === 1) {
        onChange(options[0]);
        setIsOpen(false);
        setIsLoading(false);
      }

      setDelayedInput(false);
    }
  }, [options]);

  useEffect(() => {
    helperText && setHelperTextValue(helperText);
  }, [helperText]);

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  const handleInputChange = (event: SyntheticEvent, textValue: string) => {
    setInputValue(textValue);

    clearInterval(timerRef.current);

    delayedInput && setDelayedInput(false);

    if (textValue?.length > 0) {
      setHelperTextValue('');
      setIsLoading(true);
      timerRef.current = setTimeout(async () => {
        const result = await onApiInvoke(textValue);
        setIsLoading(false);
        setOptions(defaultSuggetions && defaultSuggetions.length ? [...defaultSuggetions, ...result] : result);
      }, timeOffset);
    } else if (isRequired) {
      setHelperTextValue(`Shipment ${EMPTY_FIELD_MESSAGE}`);
      onClearValue();
      setOptions(defaultSuggetions || []);
    }
    else {
      onClearValue();
      setOptions(defaultSuggetions || []);
    }
  };

  return (
    <Autocomplete
      title={title}
      disabled={disabled}
      open={isOpen}
      renderOption={(props, option, state) => renderItem(props, option, state)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={title}
          disabled={disabled}
          helperText={helperTextValue}
          inputRef={inputRef}
          InputProps={{
            ...params.InputProps
          }}
          error={isError || !isEmptyString(helperTextValue)}
        />
      )}
      onChange={(obj: any, value: any) => {
        const val = value.id;
        
        if (val) {
          const item = options?.find((op: any) => op.name === val || op.id === parseInt(val));
          item && onChange(item);
        }
      }}
      inputValue={inputValue ?? ''}
      value={value}
      onInputChange={handleInputChange}
      options={options}
      getOptionLabel={(obj: any) => obj?.name || obj?.id?.toString() || obj || ''}
      filterOptions={(items) => items}
      loading={isLoading}
      size={SizeProps.Small}
      autoHighlight={true}
      onBlur={() => { 
        setIsLoading(false);
        setDelayedInput(false);
      }}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if(event.key === 'Enter') {
          setDelayedInput(true);
        }
      }}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
    />
  );
};